import {
  type PosthogExceptionEntry,
  type PosthogExceptionFrame,
  exceptionValue,
} from "./posthog-exception-types";

export function isInjectedBrowserBridgeMessage(value: string): boolean {
  return (
    value.includes("window.webkit.messageHandlers") ||
    value.includes("Invalid call to runtime.sendMessage(). Tab not found") ||
    value.includes(
      "Cannot read properties of undefined (reading 'bufferBelongsToIframe')",
    )
  );
}

export function isNextCurrentScriptInvariantValue(value: string): boolean {
  return value.includes(
    "Expected document.currentScript to be a <script> element",
  );
}

export function isFrozenHistoryAssignmentValue(value: string): boolean {
  return (
    value.includes(
      "Cannot assign to read only property 'pushState' of object '#<History>'",
    ) ||
    value.includes(
      "Cannot assign to read only property 'replaceState' of object '#<History>'",
    )
  );
}

export function isFirefoxIosInjectedGlobalValue(value: string): boolean {
  return value.includes("__firefox__");
}

export function isDuckDuckGoFeatureInjectionValue(value: string): boolean {
  return /feature named ['"`].+?['"`] was not found/i.test(value);
}

function frameLooksThirdParty(frame: PosthogExceptionFrame): boolean {
  if (frame.in_app === false) return true;
  const path = frame.filename ?? frame.abs_path ?? "";
  if (!path) return false;
  return !path.startsWith("app:///") && !path.includes("_next/");
}

export function isDuckDuckGoFeatureInjectionPosthog(
  first: PosthogExceptionEntry,
): boolean {
  if (!isDuckDuckGoFeatureInjectionValue(exceptionValue(first))) return false;
  const frames = first.stacktrace?.frames ?? [];
  if (frames.length === 0) return false;
  return frames.every(frameLooksThirdParty);
}

const GOOGLE_APP_USER_AGENT_TOKEN = "GSA/";

function parseUrl(file: string): URL | undefined {
  try {
    return new URL(file);
  } catch {
    return undefined;
  }
}

function frameIsCreditedToPageDocument(
  frame: PosthogExceptionFrame,
  pageOrigin: string,
): boolean {
  const url = parseUrl(frame.filename ?? frame.abs_path ?? "");
  if (!url) return false;
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (url.origin !== pageOrigin) return false;
  return !url.pathname.includes("/_next/") && !/\.[cm]?js$/i.test(url.pathname);
}

// WHY: the Google app's in-app browser injects a script that WebKit credits to the page URL, so inside that app an overflow whose every frame is the page document is the app's code.
export function isHostAppInjectedStackOverflowPosthog(
  list: Array<PosthogExceptionEntry>,
  page: { origin: string; userAgent: string },
): boolean {
  if (!page.userAgent.includes(GOOGLE_APP_USER_AGENT_TOKEN)) return false;
  const first = list[0];
  if (!first) return false;
  if (!exceptionValue(first).includes("Maximum call stack size exceeded")) {
    return false;
  }
  const frames = list.flatMap((entry) => entry.stacktrace?.frames ?? []);
  return (
    frames.length > 0 &&
    frames.every((frame) => frameIsCreditedToPageDocument(frame, page.origin))
  );
}

export function isWebkitAutofillHandlerValue(value: string): boolean {
  return value.includes("_AutofillCallbackHandler");
}
